/**
 * Creates a brand-new, empty markdown file record ("doc") on the canvas.
 *
 * A doc is just an uploaded `.md` file where we synthesize the empty File
 * ourselves: the existing upload queue mints the record + S3 object, and the
 * collaborative Tiptap editor takes over on first open (room creation, seed,
 * tiptap-enable) exactly as it does for user-uploaded markdown.
 */

import { useFileRecordUploadQueue } from "@/hooks/useFileRecordUploadQueue";
import { useOpenFileRecord } from "@/panes/file-record-detail";
import { useReactFlow, type Node } from "@xyflow/react";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";
import {
  buildFileRecordEntityForCanvas,
  defaultFileRecordNodeBox,
} from "../utils/imageDimensions";
import { attachNodeToSectionIfApplicable } from "../utils/sectionMembership";

export const DOC_DEFAULT_NAME = "New Doc";
export const DOC_CONTENT_TYPE = "text/markdown";
// A new doc is a page from its first frame: same box every other route mints.
const DOC_NODE_BOX = defaultFileRecordNodeBox(DOC_CONTENT_TYPE);
export const DOC_NODE_WIDTH = DOC_NODE_BOX.width;
export const DOC_NODE_HEIGHT = DOC_NODE_BOX.height;

/**
 * Returns a callback that places a loading ghost entity node at `position`,
 * creates the empty markdown file record through the upload queue (which
 * handles name-collision renaming against the canvas scope), swaps the ghost
 * for the real entity, and opens the file record detail pane so the user
 * lands directly in the editor.
 */
export function useCreateDocOnCanvas() {
  const { canvasId } = useCanvasRoute();
  const { addNode, removeNode, replaceNodeData } = useCanvas();
  const reactFlowInstance = useReactFlow();
  const queue = useFileRecordUploadQueue();
  const openFileRecord = useOpenFileRecord();

  return useCallback(
    (position: { x: number; y: number }) => {
      if (!canvasId) return;

      const nodeId = uuidv4();
      const newNode: Node = {
        id: nodeId,
        type: "entity",
        position,
        width: DOC_NODE_WIDTH,
        height: DOC_NODE_HEIGHT,
        data: {
          loading: true,
          entity: {
            type: "file_record",
            base: "file_record",
            platform: "cohesive",
            data: {
              id: "",
              title: DOC_DEFAULT_NAME,
              content_type: DOC_CONTENT_TYPE,
            },
          },
        },
      };
      const attached = attachNodeToSectionIfApplicable({
        node: newNode,
        nodes: reactFlowInstance.getNodes(),
        getInternalNode: (id) => reactFlowInstance.getInternalNode(id),
      });
      addNode(attached);

      // Zero bytes is a valid body — the S3 presign policy's
      // content-length-range starts at 0, and the collaborative editor
      // treats "no seed content" as an empty document.
      const file = new File([], `${DOC_DEFAULT_NAME}.md`, {
        type: DOC_CONTENT_TYPE,
      });

      void queue.enqueue(
        [{ file, contentType: DOC_CONTENT_TYPE, kind: "create" }],
        { class: "canvas", canvas_id: canvasId },
        {
          onRecordCreated: (record) => {
            const { entity } = buildFileRecordEntityForCanvas({
              record,
              contentType: DOC_CONTENT_TYPE,
            });
            replaceNodeData(nodeId, { entity });
            // Creating a doc means the user is about to write — drop them
            // straight into the editor.
            openFileRecord(record.id);
          },
          onUploadError: () => {
            removeNode(nodeId);
            toast.error("Failed to create doc");
          },
        },
      );
    },
    [
      canvasId,
      addNode,
      removeNode,
      replaceNodeData,
      reactFlowInstance,
      queue,
      openFileRecord,
    ],
  );
}
